import React, { useEffect, createElement } from 'react';
export function Head() {
  useEffect(() => {
    // Set document title
    document.title = 'WordPress Development & Web Design Services UK | Mobile App Development | Tech Vero Solutions';
    // Helper function to set or update meta tags
    const setMetaTag = (name: string, content: string, property?: boolean) => {
      const attr = property ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      meta.content = content;
    };
    // Helper function to set link tags
    const setLinkTag = (rel: string, href: string) => {
      let link = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
      if (!link) {
        link = document.createElement('link');
        link.rel = rel;
        document.head.appendChild(link);
      }
      link.href = href;
    };
    const siteUrl = 'https://techverosolutions.com';
    const description = 'Tech Vero Solutions is a leading UK web development agency offering professional WordPress development, custom web design, and mobile app development (iOS & Android). Get a free quote today! Call +44 7921 876472.';
    const title = 'WordPress Development & Web Design Services UK | Mobile App Development | Tech Vero Solutions';
    const keywords = 'WordPress development UK, web design services UK, mobile app development, hybrid app development, native app development, iOS app development, Android app development, custom website design, WordPress agency UK, web development company UK, Tech Vero Solutions';
    // Primary Meta Tags
    setMetaTag('description', description);
    setMetaTag('keywords', keywords);
    setMetaTag('author', 'Tech Vero Solutions');
    setMetaTag('robots', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    setMetaTag('language', 'English');
    setMetaTag('revisit-after', '7 days');
    setMetaTag('geo.region', 'GB');
    setMetaTag('geo.placename', 'United Kingdom');
    setMetaTag('theme-color', '#1e3a5f');
    setMetaTag('msapplication-TileColor', '#1e3a5f');
    setMetaTag('format-detection', 'telephone=yes');
    // Open Graph / Facebook
    setMetaTag('og:type', 'website', true);
    setMetaTag('og:url', siteUrl, true);
    setMetaTag('og:title', title, true);
    setMetaTag('og:description', description, true);
    setMetaTag('og:image', `${siteUrl}/og-image.jpg`, true);
    setMetaTag('og:image:width', '1200', true);
    setMetaTag('og:image:height', '630', true);
    setMetaTag('og:site_name', 'Tech Vero Solutions', true);
    setMetaTag('og:locale', 'en_GB', true);
    // Twitter
    setMetaTag('twitter:card', 'summary_large_image', true);
    setMetaTag('twitter:url', siteUrl, true);
    setMetaTag('twitter:title', title, true);
    setMetaTag('twitter:description', description, true);
    setMetaTag('twitter:image', `${siteUrl}/og-image.jpg`, true);
    // Canonical URL
    setLinkTag('canonical', siteUrl);
    // Schema.org Structured Data
    const schemas = [
    // Organization Schema
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      '@id': `${siteUrl}/#organization`,
      name: 'Tech Vero Solutions',
      url: siteUrl,
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}/logo.png`,
        width: 200,
        height: 60
      },
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+44-7921-876472',
        contactType: 'customer service',
        areaServed: 'GB',
        availableLanguage: 'English'
      },
      sameAs: ['https://wa.me/447921876472']
    },
    // LocalBusiness Schema
    {
      '@context': 'https://schema.org',
      '@type': 'ProfessionalService',
      '@id': `${siteUrl}/#localbusiness`,
      name: 'Tech Vero Solutions',
      description: 'Professional WordPress development, web design, and mobile app development services in the UK.',
      url: siteUrl,
      telephone: '+44-7921-876472',
      priceRange: '££',
      areaServed: {
        '@type': 'Country',
        name: 'United Kingdom'
      },
      serviceArea: {
        '@type': 'Country',
        name: 'United Kingdom'
      },
      knowsLanguage: 'English',
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Web Development Services',
        itemListElement: [{
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'WordPress Development',
            description: 'Custom WordPress website development, theme customization, plugin development, and WooCommerce solutions.'
          }
        }, {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Web Design',
            description: 'Professional UI/UX design, responsive web design, and brand-focused website creation.'
          }
        }, {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Mobile App Development',
            description: 'Native iOS and Android app development, hybrid mobile applications using React Native and Flutter.'
          }
        }]
      }
    },
    // WebSite Schema
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      '@id': `${siteUrl}/#website`,
      url: siteUrl,
      name: 'Tech Vero Solutions',
      description: description,
      publisher: {
        '@id': `${siteUrl}/#organization`
      },
      inLanguage: 'en-GB'
    },
    // Service List Schema
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListElement: [{
        '@type': 'ListItem',
        position: 1,
        item: {
          '@type': 'Service',
          name: 'WordPress Development Services',
          description: 'Custom WordPress development including theme development, plugin creation, WooCommerce setup, and ongoing maintenance.',
          provider: {
            '@id': `${siteUrl}/#organization`
          },
          areaServed: 'United Kingdom',
          serviceType: 'WordPress Development'
        }
      }, {
        '@type': 'ListItem',
        position: 2,
        item: {
          '@type': 'Service',
          name: 'Web Design Services',
          description: 'Professional web design services including UI/UX design, responsive design, and landing page design.',
          provider: {
            '@id': `${siteUrl}/#organization`
          },
          areaServed: 'United Kingdom',
          serviceType: 'Web Design'
        }
      }, {
        '@type': 'ListItem',
        position: 3,
        item: {
          '@type': 'Service',
          name: 'Mobile App Development',
          description: 'Native and hybrid mobile app development for iOS and Android using React Native, Flutter, Swift, and Kotlin.',
          provider: {
            '@id': `${siteUrl}/#organization`
          },
          areaServed: 'United Kingdom',
          serviceType: 'Mobile App Development'
        }
      }]
    },
    // FAQ Schema
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [{
        '@type': 'Question',
        name: 'What WordPress development services do you offer?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'We offer comprehensive WordPress services including custom theme development, plugin development, WooCommerce setup, website migrations, performance optimization, security hardening, and ongoing maintenance and support.'
        }
      }, {
        '@type': 'Question',
        name: 'Do you develop both native and hybrid mobile apps?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes, we develop both native apps (using Swift for iOS and Kotlin for Android) and hybrid/cross-platform apps using React Native and Flutter.'
        }
      }, {
        '@type': 'Question',
        name: 'How much does a website cost?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Website costs vary based on complexity. A basic WordPress website starts from £500, while custom web applications range from £2,000 to £10,000+. Contact us for a free quote.'
        }
      }, {
        '@type': 'Question',
        name: 'How long does it take to build a website?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'A standard WordPress website typically takes 2-4 weeks. Custom web applications and mobile apps take 6-12 weeks depending on complexity.'
        }
      }, {
        '@type': 'Question',
        name: 'Do you provide ongoing support and maintenance?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes, we offer comprehensive maintenance packages including security updates, backups, performance monitoring, content updates, and technical support via WhatsApp.'
        }
      }]
    },
    // BreadcrumbList Schema
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [{
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: siteUrl
      }]
    }];
    // Add schema scripts
    schemas.forEach((schema, index) => {
      const existingScript = document.querySelector(`script[data-schema="${index}"]`);
      if (existingScript) {
        existingScript.remove();
      }
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-schema', index.toString());
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });
    // Cleanup function
    return () => {
      schemas.forEach((_, index) => {
        const script = document.querySelector(`script[data-schema="${index}"]`);
        if (script) {
          script.remove();
        }
      });
    };
  }, []);
  return null;
}